import React, { useState, useCallback } from 'react';
import moment from 'moment';
import firebaseApp from '../../../firebase-app';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import Paper from '@mui/material/Paper';
import { doc, setDoc } from 'firebase/firestore';
import MappingsHeader from './mappings-header';
import { useSnackbar } from 'notistack';
import MappingDialog from './mapping-dialog';
import MappingsListTeams from './mapping-list-teams';
import MappingsListLeagues from './mapping-list-leagues';
import ScenePage from '../../common/scene/scene-page';

export type MappingSelectType = 'teams' | 'leagues';

export interface MappingTeams {
  abbrv: string;
  displayName: string;
  gender: 'men' | 'women';
  name: string;
  sport: 'FOOTBALL' | 'FUTSAL';
  everySportId: number;
  fogisId: number;
  forzasysId: number;
  smcId: number;
}

export interface MappingLeagues {
  id: string;
  configLeagueName: string;
  configSeasonStartYear: number;
  leagueDisplayName: string;
  everysportLeagueId: number;
  everysportIds: number[];
  fogisIds: number[];
  smcId: number;
  teams: string[];
}

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    height: 'calc(100% - 90px)',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

const MappingsList = () => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<MappingSelectType>('teams');

  const onSave = useCallback(
    async (id: string) => {
      const newRow: MappingLeagues | MappingTeams =
        selectedType === 'teams'
          ? {
              abbrv: id,
              displayName: id,
              everySportId: 0,
              fogisId: 0,
              forzasysId: 0,
              smcId: 0,
              gender: 'men',
              name: id,
              sport: 'FOOTBALL',
            }
          : {
              id: id,
              configLeagueName: id,
              configSeasonStartYear: moment().year(),
              leagueDisplayName: id,
              fogisIds: [0],
              everysportLeagueId: 0,
              everysportIds: [0],
              smcId: 0,
              teams: ['AIK'],
            };
      try {
        await setDoc(doc(firebaseApp.db, `mappings/${selectedType}/all/${id}`), newRow as any);
        setShowAddDialog(false);
      } catch (error) {
        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, selectedType, strings]
  );

  return (
    <ScenePage title={strings.appBar.mappings} breadCrumps={[strings.appBar.superAdmin]}>
      <MappingsHeader
        onAddRow={() => setShowAddDialog(true)}
        onSelectedType={(selectedType) => setSelectedType(selectedType)}
      />
      <Paper elevation={2} sx={styles.container}>
        {selectedType === 'teams' && <MappingsListTeams />}
        {selectedType === 'leagues' && <MappingsListLeagues />}
      </Paper>
      {showAddDialog && (
        <MappingDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onSave={onSave}
        />
      )}
    </ScenePage>
  );
};

export default MappingsList;
